import { BaseStateInterface } from './basestate.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { QuestionnaireAction, QuestionnaireState } from './questionnaire.state';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { PricingPlanDto } from 'app/@types/dto/pricing-plan-dto.model';
import { TagEntitiesDto } from 'app/@types/dto/tag-entities-dto.model';
import { TagHttpService } from '@services/datasources/tag-http.service';
import { QuestionnaireConfigAction } from './questionnaire.config.state';
import { AnalyticalCategoryDto } from 'app/@types/dto/analytical-category-dto.model';
import { AnalyticalCategoriesHttpService } from '@services/datasources/analytical-categories-http.service';
import { ResultWebSocketService } from '@services/datasources/result-web-socket.service';
import { HubAccessDeniedError } from '@services/datasources/hub-access-denied.error';
import { EmissionFactorItemsAction } from './organizationItems.state';
import { AdvancedMeasureAction } from './advanced-measure.state';
import { QuestionnaireCsrdHttpService } from '@services/datasources/questionnaireCsrd-http.service';
import { EmissionFactorHttpService } from '@services/datasources/emission-factor-http.service';

export interface OrganizationModel extends BaseStateInterface {
    organization: OrganizationDto | undefined;
    questionnaires: QuestionnaireDto[] | undefined;
    availableOrganization: OrganizationDto[];
    availableYears: number[] | undefined;
    entities: OrganizationEntityDto[] | undefined;
    organizationPricingPlan: OrganizationPricingPlanDto | undefined;
    pricingPlan: PricingPlanDto | undefined;
    tags: TagEntitiesDto[] | undefined;
    analyticCategories: AnalyticalCategoryDto[] | undefined;
}

// eslint-disable-next-line @typescript-eslint/no-namespace -- NGXS action grouping requires namespace for combined value+type semantics
export namespace OrganizationAction {
    export class Activate {
        public static type = '[Organization UI] Activate Questionnaire';

        constructor(public organizationId: string) {}
    }

    export class Deactivate {
        public static type = '[Organization UI] Deactivate Organization';
    }

    export class Refresh {
        public static type = '[Organization UI] Activate Questionnaire';
    }

    export class AddQuestionnairesFromPeriodAction {
        public static type = '[Organization UI] Add Questionnaires from a period';

        constructor(
            public organizationId: string,
            public year: number,
            public callCompletion: Subject<QuestionnaireDto[] | undefined>,
        ) {}
    }

    export class CreateEntitiesAction {
        public static type = '[Organization UI] Add Questionnaires from an entity';

        constructor(
            public organizationId: string,
            public entity: OrganizationEntityDto,
            public callCompletion: Subject<OrganizationEntityDto | undefined>,
        ) {}
    }

    export class DeleteQuestionnairesFromPeriodAction {
        public static type = '[Organization UI] Soft delete Questionnaires from a period';

        constructor(
            public organizationId: string,
            public year: number,
            public activeQuestionnaire: QuestionnaireDto,
            public callCompletion: Subject<QuestionnaireDto[] | undefined>,
        ) {}
    }

    export class DeleteEntityAction {
        public static type =
            '[Organization UI] Soft delete Questionnaires from an entity and delete the entity itself.';

        constructor(
            public organizationId: string,
            public entityid: string,
            public activeQuestionnaire: QuestionnaireDto,
            public callCompletion: Subject<QuestionnaireDto[] | undefined>,
        ) {}
    }

    export class EditQuestionnairesPeriodAction {
        public static type = '[Organization UI] Change the period of all the questionnaires with a selected periods';

        constructor(
            public organizationId: string,
            public currentYear: number,
            public newYear: number,
            public currentQuestionnaire: QuestionnaireDto,
            public callCompletion: Subject<QuestionnaireDto[] | undefined>,
        ) {}
    }

    export class UpdateEntitieAction {
        public static type = '[Organization UI] Update the properties of an entity';

        constructor(
            public organizationId: string,
            public entityId: string,
            public entity: OrganizationEntityDto,
            public callCompletion: Subject<OrganizationEntityDto | undefined>,
        ) {}
    }

    export class SetTags {
        public static type = '[Organization UI] Set the tags of the organization';

        constructor(public tags: TagEntitiesDto[]) {}
    }

    export class CreateAnalyticCategory {
        public static type = '[Organization UI] Create Analytic Category';

        constructor(
            public organizationId: string,
            public analyticCategory: AnalyticalCategoryDto,
            public callCompletion: Subject<void>,
        ) {}
    }

    export class UpdateAnalyticCategory {
        public static type = '[Organization UI] Update Analytic Category';

        constructor(
            public organizationId: string,
            public analyticCategory: AnalyticalCategoryDto,
            public callCompletion: Subject<void>,
        ) {}
    }

    export class DeleteAnalyticCategory {
        public static type = '[Organization UI] Delete Analytic Category';

        constructor(
            public organizationId: string,
            public analyticCategoryId: string,
            public callCompletion: Subject<void>,
        ) {}
    }

    export class UpdateQuestionnaireList {
        public static type = '[Organization UI] Update the list of questionnaires';

        constructor(public questionnaire: QuestionnaireDto) {}
    }

    // Bulk replacement of the questionnaires slice. Used by flows that refetch the whole list
    // (e.g., matrix activation re-pulling fresh summaryDocs) — avoids dispatching N
    // UpdateQuestionnaireList actions in a loop, which would flip OnPush change detection
    // for every entry.
    export class SetQuestionnaires {
        public static type = '[Organization UI] Replace the list of questionnaires';

        constructor(public questionnaires: QuestionnaireDto[]) {}
    }
}

@State<OrganizationModel>({
    name: 'organization',
    defaults: {
        organization: undefined,
        availableOrganization: [],
        questionnaires: [],
        availableYears: [],
        entities: [],
        organizationPricingPlan: undefined,
        pricingPlan: undefined,
        tags: [],
        analyticCategories: [],
        isInit: false,
        isLoading: false,
        errors: [],
    },
})
@Injectable()
export class OrganizationState {
    private isInit: boolean = false;
    /** Cancels in-flight HTTP from the current / previous Activate when Deactivate runs or a new Activate starts. */
    private readonly activateTeardown$ = new Subject<void>();
    private activateGeneration = 0;
    constructor(
        private questionnaireService: QuestionnaireHttpService,
        private store: Store,
        private adminHttpService: AdminHTTPService,
        private tagHttpService: TagHttpService,
        private analyticalCategoriesHttpService: AnalyticalCategoriesHttpService,
        private resultWebSocketService: ResultWebSocketService,
        private questionnaireCsrdService: QuestionnaireCsrdHttpService,
        private emissionFactorHttpService: EmissionFactorHttpService,
    ) {}

    @Action(OrganizationAction.Activate)
    activate(ctx: StateContext<OrganizationModel>, action: OrganizationAction.Activate) {
        this.activateTeardown$.next();
        if (!action.organizationId?.trim()) {
            this.activateGeneration++;
            ctx.patchState({
                isLoading: false,
            });
            return;
        }
        ctx.patchState({
            isLoading: true,
        });
        const generation = ++this.activateGeneration;
        const untilActivate = this.activateTeardown$;
        // Initialize the web socket connection and join the organization group
        this.resultWebSocketService.startConnection().then(() => {
            if (generation !== this.activateGeneration) {
                return;
            }
            this.resultWebSocketService.joinOrganizationGroup(action.organizationId).catch((err: unknown) => {
                if (err instanceof HubAccessDeniedError) {
                    console.warn('[OrganizationState] SignalR org group join denied:', err.message);
                }
            });
        });
        //Retreive the organization from the id.
        this.questionnaireService
            .getOrganization(action.organizationId)
            .pipe(takeUntil(untilActivate))
            .subscribe({
                next: (organization) => {
                    if (organization.uid) {
                        this.questionnaireService
                            .getEntities(organization.uid)
                            .pipe(takeUntil(untilActivate))
                            .subscribe({
                                next: (entities) => {
                                    ctx.patchState({
                                        organization: organization,
                                        entities: entities.filter((entity) => !entity.deleted),
                                    });
                                },
                            });
                    }
                },
            });

        //Retreive PricingPlan and orgPP to have access to the validitym anf tags and analytic categories if expert license.
        combineLatest([
            this.adminHttpService.getOrganizationPricingPlan(action.organizationId),
            this.adminHttpService.getPricingPlan(action.organizationId),
        ])
            .pipe(takeUntil(untilActivate))
            .subscribe({
                next: ([orgPP, PP]) => {
                    if (orgPP.licenseDoc.expert) {
                        combineLatest([
                            this.analyticalCategoriesHttpService.getAll(action.organizationId),
                            this.tagHttpService.getAll(action.organizationId),
                        ])
                            .pipe(takeUntil(untilActivate))
                            .subscribe(([analyticCategories, tags]) => {
                                ctx.patchState({
                                    analyticCategories: analyticCategories,
                                    tags: tags,
                                    organizationPricingPlan: orgPP,
                                    pricingPlan: PP,
                                });
                            });
                        this.store.dispatch(
                            new EmissionFactorItemsAction.SetEmissionFactorDatabases(action.organizationId),
                        );
                    } else {
                        ctx.patchState({
                            organizationPricingPlan: orgPP,
                            pricingPlan: PP,
                        });
                    }
                },
            });

        //Retreive the questionnaires
        this.questionnaireService
            .getAll(action.organizationId)
            .pipe(takeUntil(untilActivate))
            .subscribe((questionnairesQuestionInstances) => {
                const questionnaires = questionnairesQuestionInstances.map(({ questionnaire }) => questionnaire);
                const possibleYears = Array.from({ length: 2050 - 2018 + 1 }, (_, index) => 2018 + index);
                const nonDeletedQuestionnaires = questionnaires.filter((quest) => !quest.deleted);
                const existingYears = nonDeletedQuestionnaires.map((quest) => quest.refYear);
                const availableYears = possibleYears.filter((year) => !existingYears.includes(year));
                ctx.patchState({
                    questionnaires: questionnaires,
                    availableYears: availableYears,
                });
            });
        // Retreive the official categories and emission factor databases
        this.store.dispatch(new EmissionFactorItemsAction.SetOfficialCategories());
        this.store.dispatch(new EmissionFactorItemsAction.InvalidateData());
        this.store.dispatch(new AdvancedMeasureAction.ClearState());
    }

    @Action(OrganizationAction.AddQuestionnairesFromPeriodAction)
    CreateQuestionnairesFromPeriod(
        ctx: StateContext<OrganizationModel>,
        action: OrganizationAction.AddQuestionnairesFromPeriodAction,
    ) {
        combineLatest([
            this.questionnaireService.createQuestionnaireFromPeriod(action.organizationId, action.year),
            this.questionnaireCsrdService.createQuestionnaireCsrd(action.organizationId, action.year),
        ]).subscribe({
            next: ([createdQuestionnaires, _createdQuestionnaireCsrd]) => {
                let currentQuestionnaires = ctx.getState().questionnaires || [];
                currentQuestionnaires = currentQuestionnaires.filter((quest) => quest.refYear !== action.year); // If the year was undelete we remove the questionnaires before adding new ones. (Cleaning)
                const currentAvailableYears = ctx.getState().availableYears || [];
                ctx.patchState({
                    questionnaires: [...currentQuestionnaires, ...createdQuestionnaires],
                    availableYears: currentAvailableYears.filter((year) => year !== action.year),
                });
                action.callCompletion.next(createdQuestionnaires);
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.CreateEntitiesAction)
    CreateEntities(ctx: StateContext<OrganizationModel>, action: OrganizationAction.CreateEntitiesAction) {
        this.questionnaireService.createEntity(action.organizationId, action.entity).subscribe({
            next: (createdEntity) => {
                this.questionnaireService.getAll(action.organizationId).subscribe((questionnairesQuestionInstances) => {
                    const questionnaires = questionnairesQuestionInstances.map(({ questionnaire }) => questionnaire);
                    const currentEntities = ctx.getState().entities || [];
                    ctx.patchState({
                        questionnaires: questionnaires,
                        entities: [...currentEntities, createdEntity],
                    });
                    action.callCompletion.next(createdEntity);
                });
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.DeleteQuestionnairesFromPeriodAction)
    DeleteQuestionnairesFromPeriod(
        ctx: StateContext<OrganizationModel>,
        action: OrganizationAction.DeleteQuestionnairesFromPeriodAction,
    ) {
        combineLatest([
            this.questionnaireCsrdService.deleteQuestionnaireFromPeriod(action.organizationId, action.year),
            this.questionnaireService.deleteQuestionnaireFromPeriod(action.organizationId, action.year),
        ]).subscribe({
            next: ([_, deletedQuestionnaires]) => {
                const currentQuestionnaires = ctx.getState().questionnaires || [];
                let updatedQuestionnaires = currentQuestionnaires.filter((quest) => quest.refYear !== action.year);
                updatedQuestionnaires = [...updatedQuestionnaires, ...deletedQuestionnaires]; //Even if SD, they should still be in questionnaires state.
                const currentAvailableYears = ctx.getState().availableYears || [];
                currentAvailableYears.push(action.year);
                ctx.patchState({
                    questionnaires: updatedQuestionnaires,
                    availableYears: currentAvailableYears.sort((a, b) => a - b),
                });
                action.callCompletion.next(deletedQuestionnaires);
                const sortedquestionaires = updatedQuestionnaires
                    .filter(
                        (quest) =>
                            !quest.deleted &&
                            quest.organizationEntityId === action.activeQuestionnaire.organizationEntityId,
                    )
                    .sort((a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime());
                this.store.dispatch(
                    new QuestionnaireAction.Activate(
                        action.organizationId,
                        sortedquestionaires[0].refYear,
                        sortedquestionaires[0].organizationEntityId,
                    ),
                );
                this.store.dispatch(
                    new QuestionnaireConfigAction.Activate(
                        action.organizationId,
                        sortedquestionaires[0].organizationEntityId,
                    ),
                );
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.DeleteEntityAction)
    DeleteEntity(ctx: StateContext<OrganizationModel>, action: OrganizationAction.DeleteEntityAction) {
        this.questionnaireService.deleteEntity(action.organizationId, action.entityid).subscribe({
            next: (deletedQuestionnaires) => {
                const currentQuestionnaires = ctx.getState().questionnaires || [];
                let updatedQuestionnaires = currentQuestionnaires.filter(
                    (quest) => quest.organizationEntityId !== action.entityid,
                );
                updatedQuestionnaires = [...updatedQuestionnaires, ...deletedQuestionnaires]; //Even if SD, they should still be in questionnaires state.
                const updatedEntites = ctx.getState().entities?.filter((entity) => entity.id !== action.entityid);
                //Remove the entity from the analytic categories
                const updatedAnalyticCategories = ctx
                    .getState()
                    .analyticCategories?.map((cat) => {
                        return {
                            ...cat,
                            entityIds: cat.entityIds?.filter((id) => id !== action.entityid),
                        };
                    })
                    .filter((cat) => (cat.entityIds?.length && cat.entityIds?.length > 0) || !cat.entityIds);
                ctx.patchState({
                    questionnaires: updatedQuestionnaires,
                    entities: updatedEntites,
                    analyticCategories: updatedAnalyticCategories,
                });
                action.callCompletion.next(deletedQuestionnaires);
                const sortedquestionaires = updatedQuestionnaires
                    .filter((quest) => !quest.deleted && quest.refYear === action.activeQuestionnaire.refYear)
                    .sort((a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime());
                this.store.dispatch(
                    new QuestionnaireAction.Activate(
                        action.organizationId,
                        sortedquestionaires[0].refYear,
                        sortedquestionaires[0].organizationEntityId,
                    ),
                );
                this.store.dispatch(
                    new QuestionnaireConfigAction.Activate(
                        action.organizationId,
                        sortedquestionaires[0].organizationEntityId,
                    ),
                );
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.UpdateEntitieAction)
    UpdateEntitie(ctx: StateContext<OrganizationModel>, action: OrganizationAction.UpdateEntitieAction) {
        this.questionnaireService.updateEntity(action.organizationId, action.entityId, action.entity).subscribe({
            next: (modifiedEntity) => {
                let currentEntities = ctx.getState().entities || [];
                //Remove the entity
                currentEntities = currentEntities.filter((entity) => entity.id !== action.entityId);
                const updatedEntities = [...currentEntities, modifiedEntity];
                ctx.patchState({
                    entities: updatedEntities,
                });
                const currentQuestionnaire = this.store.selectSnapshot(QuestionnaireState.getQuestionnaire);
                //We only need to update the config if the modified entity is the one of our active questionnaire.
                if (currentQuestionnaire && currentQuestionnaire.organizationEntityId === action.entityId) {
                    this.store.dispatch(new QuestionnaireConfigAction.Activate(action.organizationId, action.entityId));
                }
                action.callCompletion.next(modifiedEntity);
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.EditQuestionnairesPeriodAction)
    EditQuestionnairesPeriod(
        ctx: StateContext<OrganizationModel>,
        action: OrganizationAction.EditQuestionnairesPeriodAction,
    ) {
        combineLatest([
            this.questionnaireCsrdService.editQuestionnairePeriod(
                action.organizationId,
                action.currentYear,
                action.newYear,
            ),
            this.questionnaireService.editQuestionnairesPeriod(
                action.organizationId,
                action.currentYear,
                action.newYear,
            ),
        ]).subscribe({
            next: ([_, modifiedQuestionnaires]) => {
                let currentQuestionnaires = ctx.getState().questionnaires || [];
                currentQuestionnaires = currentQuestionnaires.filter(
                    (quest) => quest.refYear !== action.currentYear && quest.refYear !== action.newYear,
                );
                const currentAvailableYears = ctx.getState().availableYears || [];
                currentAvailableYears.push(action.currentYear);
                ctx.patchState({
                    questionnaires: [...currentQuestionnaires, ...modifiedQuestionnaires],
                    availableYears: currentAvailableYears
                        .filter((year) => year !== action.newYear)
                        .sort((a, b) => a - b),
                    entities: ctx.getState().entities,
                });
                this.store.dispatch(
                    new QuestionnaireAction.Activate(
                        action.organizationId,
                        action.newYear,
                        action.currentQuestionnaire.organizationEntityId,
                    ),
                );
                action.callCompletion.next(modifiedQuestionnaires);
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.Deactivate) deactivate(
        ctx: StateContext<OrganizationModel>,
        _action: OrganizationAction.Deactivate,
    ) {
        this.activateTeardown$.next();
        this.activateGeneration++;
        ctx.patchState({
            organization: undefined,
            entities: undefined,
            organizationPricingPlan: undefined,
            pricingPlan: undefined,
            questionnaires: undefined,
            availableYears: undefined,
            tags: undefined,
            analyticCategories: undefined,
        });
    }

    @Action(OrganizationAction.SetTags)
    CreateTag(ctx: StateContext<OrganizationModel>, action: OrganizationAction.SetTags) {
        ctx.patchState({
            tags: action.tags,
        });
    }

    //Analytic categories
    @Action(OrganizationAction.CreateAnalyticCategory)
    CreateAnalyticCategory(ctx: StateContext<OrganizationModel>, action: OrganizationAction.CreateAnalyticCategory) {
        this.analyticalCategoriesHttpService.create(action.analyticCategory, action.organizationId).subscribe({
            next: (analyticCategory: AnalyticalCategoryDto) => {
                const currentCategories = ctx.getState().analyticCategories || [];
                ctx.patchState({
                    analyticCategories: [...currentCategories, analyticCategory],
                });
                action.callCompletion.next();
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.UpdateAnalyticCategory)
    UpdateAnalyticCategory(ctx: StateContext<OrganizationModel>, action: OrganizationAction.UpdateAnalyticCategory) {
        this.analyticalCategoriesHttpService.update(action.analyticCategory, action.organizationId).subscribe({
            next: (updatedCategory: AnalyticalCategoryDto) => {
                const currentCategories = ctx.getState().analyticCategories || [];
                const updatedCategories = currentCategories.map((cat) =>
                    cat.id === updatedCategory.id ? updatedCategory : cat,
                );
                ctx.patchState({
                    analyticCategories: updatedCategories,
                });
                action.callCompletion.next();
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.DeleteAnalyticCategory)
    DeleteAnalyticCategory(ctx: StateContext<OrganizationModel>, action: OrganizationAction.DeleteAnalyticCategory) {
        this.analyticalCategoriesHttpService.delete(action.analyticCategoryId, action.organizationId).subscribe({
            next: () => {
                const currentCategories = ctx.getState().analyticCategories || [];
                const updatedCategories = currentCategories.filter((cat) => cat.id !== action.analyticCategoryId);
                ctx.patchState({
                    analyticCategories: updatedCategories,
                });
                action.callCompletion.next();
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.UpdateQuestionnaireList)
    UpdateQuestionnaireList(ctx: StateContext<OrganizationModel>, action: OrganizationAction.UpdateQuestionnaireList) {
        const questionnaires = ctx.getState().questionnaires;
        const updatedQuestionnaires = questionnaires?.map((quest) => {
            if (quest.id === action.questionnaire.id) {
                return action.questionnaire;
            }
            return quest;
        });
        ctx.patchState({
            questionnaires: updatedQuestionnaires,
        });
    }

    @Action(OrganizationAction.SetQuestionnaires)
    setQuestionnaires(ctx: StateContext<OrganizationModel>, action: OrganizationAction.SetQuestionnaires) {
        ctx.patchState({ questionnaires: action.questionnaires });
    }

    @Selector([OrganizationState])
    static getOrganization(state: OrganizationModel) {
        return state.organization;
    }

    @Selector([OrganizationState])
    static getQuestionnaires(state: OrganizationModel) {
        return state.questionnaires;
    }

    @Selector([OrganizationState])
    static getAvailableYears(state: OrganizationModel) {
        return state.availableYears;
    }

    @Selector([OrganizationState])
    static getEntities(state: OrganizationModel) {
        return state.entities;
    }

    @Selector([OrganizationState])
    static getOrganizationPricingPlan(state: OrganizationModel) {
        return state.organizationPricingPlan;
    }

    @Selector([OrganizationState])
    static getPricingPlan(state: OrganizationModel) {
        return state.pricingPlan;
    }

    @Selector([OrganizationState])
    static getTagsOfOrganization(state: OrganizationModel) {
        return state.tags;
    }

    @Selector([OrganizationState])
    static getAnalyticCategories(state: OrganizationModel) {
        return state.analyticCategories;
    }
}
